import React, { useEffect, useMemo, useState } from "react";
import { Window, Loading, EmptyState } from "../../components/VB";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,
} from "recharts";
import { CalendarRange, Globe, TrendingUp, Users, Code2 } from "lucide-react";

const usd = (n) => "$ " + parseFloat(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const num = (n) => (parseFloat(n || 0)).toLocaleString("id-ID");
const COLORS = ["#003399", "#0055cc", "#4f8a10", "#e88800", "#d8000c", "#8a2be2", "#00a8a8", "#666"];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const PRESETS = [
  { key: "7d",  label: "7 Hari",   from: () => daysAgo(6),   granularity: "day" },
  { key: "30d", label: "30 Hari",  from: () => daysAgo(29),  granularity: "day" },
  { key: "3m",  label: "3 Bulan",  from: () => daysAgo(89),  granularity: "week" },
  { key: "12m", label: "12 Bulan", from: () => daysAgo(364), granularity: "month" },
  { key: "5y",  label: "5 Tahun",  from: () => daysAgo(365 * 5), granularity: "year" },
  { key: "all", label: "Semua",    from: () => "", granularity: "month" },
];

export default function LaporanPembuatanWeb() {
  const [granularity, setGranularity] = useState("day");
  const [tanggalMulai, setTanggalMulai] = useState(daysAgo(29));
  const [tanggalAkhir, setTanggalAkhir] = useState(today());
  const [preset, setPreset] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { granularity };
      if (tanggalMulai) params.tanggal_mulai = tanggalMulai;
      if (tanggalAkhir) params.tanggal_akhir = tanggalAkhir;
      const { data } = await api.get("/projects/laporan-pembuatan", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [granularity, tanggalMulai, tanggalAkhir]);

  const applyPreset = (p) => {
    setPreset(p.key);
    setGranularity(p.granularity);
    setTanggalMulai(p.from());
    setTanggalAkhir(today());
  };

  const series = data?.series || [];
  const maxCount = useMemo(() => Math.max(1, ...series.map((s) => s.count)), [series]);
  const avgPerBucket = series.length ? (data.total_count / series.length).toFixed(1) : "0";

  const granLabel = { day: "Harian", week: "Mingguan", month: "Bulanan", year: "Tahunan" }[granularity];

  return (
    <Window title="Laporan Pembuatan Website">
      {/* Filter bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#003399" }}>
          <CalendarRange className="w-4 h-4 inline mr-1" /> Rentang:
        </span>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={preset === p.key ? "vb-btn-primary" : "vb-btn"}
            style={{ padding: "3px 10px", fontSize: 11 }}
            onClick={() => applyPreset(p)}
            data-testid={`preset-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        <span style={{ borderLeft: "1px solid #ccc", height: 20, margin: "0 6px" }} />
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
          Dari:
          <input
            type="date"
            className="vb-input"
            style={{ width: 130 }}
            value={tanggalMulai}
            onChange={(e) => { setTanggalMulai(e.target.value); setPreset(""); }}
            data-testid="filter-tgl-mulai"
          />
        </label>
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
          Sampai:
          <input
            type="date"
            className="vb-input"
            style={{ width: 130 }}
            value={tanggalAkhir}
            onChange={(e) => { setTanggalAkhir(e.target.value); setPreset(""); }}
            data-testid="filter-tgl-akhir"
          />
        </label>
        <span style={{ borderLeft: "1px solid #ccc", height: 20, margin: "0 6px" }} />
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
          Granularitas:
          <select
            className="vb-select"
            style={{ width: 130 }}
            value={granularity}
            onChange={(e) => { setGranularity(e.target.value); setPreset(""); }}
            data-testid="filter-granularity"
          >
            <option value="day">Day-to-Day</option>
            <option value="week">Week-to-Week</option>
            <option value="month">Month-to-Month</option>
            <option value="year">Year-to-Year</option>
          </select>
        </label>
      </div>

      {loading ? <Loading /> : !data ? <EmptyState message="Belum ada data laporan" /> : (
        <>
          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div className="vb-statcard">
              <div className="vb-statcard-label"><Globe className="w-3 h-3 inline mr-1" />Total Website Dibuat</div>
              <div className="vb-statcard-value" style={{ color: "#003399" }} data-testid="stat-total-count">{num(data.total_count)}</div>
            </div>
            <div className="vb-statcard">
              <div className="vb-statcard-label"><TrendingUp className="w-3 h-3 inline mr-1" />Total Revenue</div>
              <div className="vb-statcard-value" style={{ color: "#003399", fontSize: 15 }} data-testid="stat-revenue">{usd(data.total_revenue_usd)}</div>
            </div>
            <div className="vb-statcard">
              <div className="vb-statcard-label">Terbayar</div>
              <div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 15 }}>{usd(data.total_paid_usd)}</div>
            </div>
            <div className="vb-statcard">
              <div className="vb-statcard-label">Outstanding</div>
              <div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 15 }}>{usd(data.total_outstanding_usd)}</div>
            </div>
            <div className="vb-statcard">
              <div className="vb-statcard-label">Rata-rata / {granLabel.replace("an", "")}</div>
              <div className="vb-statcard-value">{avgPerBucket}</div>
            </div>
            <div className="vb-statcard">
              <div className="vb-statcard-label">Peak {granLabel}</div>
              <div className="vb-statcard-value" style={{ color: "#e88800" }}>{maxCount}</div>
            </div>
          </div>

          {/* Chart 1: Time Series (Bar + Line combo) */}
          <div style={{ background: "white", border: "1px solid #828790", padding: 10, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#003399" }}>
              📈 Trend Pembuatan Website ({granLabel})
              {tanggalMulai && tanggalAkhir && <span style={{ fontSize: 11, color: "#666", fontWeight: 400, marginLeft: 8 }}>{tanggalMulai} → {tanggalAkhir}</span>}
            </div>
            {series.length === 0 ? <EmptyState message="Tidak ada data pada rentang tersebut" /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={series} margin={{ top: 10, right: 20, bottom: 30, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="periode" tick={{ fontSize: 10 }} angle={series.length > 15 ? -45 : 0} textAnchor={series.length > 15 ? "end" : "middle"} interval={0} height={series.length > 15 ? 60 : 30} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: "Jumlah Web", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#003399" } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} label={{ value: "Revenue ($)", angle: 90, position: "insideRight", style: { fontSize: 11, fill: "#4f8a10" } }} />
                  <Tooltip formatter={(v, name) => name === "Revenue" ? [usd(v), name] : [num(v), name]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" name="Jumlah Website" fill="#003399" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#4f8a10" strokeWidth={2} dot={{ r: 3 }} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Chart 2: Cumulative Area */}
          {series.length > 0 && (
            <div style={{ background: "white", border: "1px solid #828790", padding: 10, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#003399" }}>
                📊 Kumulatif Pembuatan (running total)
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={series.reduce((acc, cur, i) => {
                  const prev = i > 0 ? acc[i - 1].kumulatif : 0;
                  acc.push({ periode: cur.periode, kumulatif: prev + cur.count });
                  return acc;
                }, [])}>
                  <defs>
                    <linearGradient id="colorCum" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0055cc" stopOpacity={0.7} />
                      <stop offset="95%" stopColor="#0055cc" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="periode" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => num(v)} />
                  <Area type="monotone" dataKey="kumulatif" stroke="#0055cc" strokeWidth={2} fill="url(#colorCum)" name="Total Kumulatif" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Breakdowns 2 columns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {/* By Type */}
            <div style={{ background: "white", border: "1px solid #828790", padding: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#003399" }}>
                🌐 Distribusi per Type Web
              </div>
              {(data.by_type || []).length === 0 ? <EmptyState message="Tidak ada data" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={data.by_type}
                      dataKey="count"
                      nameKey="nama"
                      cx="50%" cy="50%" outerRadius={80}
                      label={(e) => `${e.nama} (${e.count})`}
                      labelLine={false}
                    >
                      {data.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => num(v)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* By Developer */}
            <div style={{ background: "white", border: "1px solid #828790", padding: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#003399" }}>
                <Code2 className="w-3 h-3 inline mr-1" /> Top Developer
              </div>
              {(data.by_developer || []).length === 0 ? <EmptyState message="Tidak ada data" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={(data.by_developer || []).slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="nama" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip formatter={(v) => num(v)} />
                    <Bar dataKey="count" fill="#4f8a10" name="Jumlah Web" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* By Client */}
          <div style={{ background: "white", border: "1px solid #828790", padding: 10, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#003399" }}>
              <Users className="w-3 h-3 inline mr-1" /> Top 10 Client
            </div>
            {(data.by_client || []).length === 0 ? <EmptyState message="Tidak ada data" /> : (
              <ResponsiveContainer width="100%" height={Math.max(220, ((data.by_client || []).slice(0, 10).length) * 28 + 40)}>
                <BarChart data={(data.by_client || []).slice(0, 10)} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="nama" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v, n) => n === "Revenue" ? [usd(v), n] : [num(v), n]} />
                  <Legend />
                  <Bar dataKey="count" fill="#003399" name="Jumlah Web" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Raw table */}
          <div style={{ background: "#ECE9D8", padding: 6, border: "1px solid #828790", marginBottom: 4, fontWeight: 700, fontSize: 12 }}>
            Detail Data ({granLabel})
          </div>
          {series.length === 0 ? <EmptyState message="Tidak ada data" /> : (
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Periode</th>
                    <th style={{ textAlign: "right" }}>Jumlah Web</th>
                    <th style={{ textAlign: "right" }}>Revenue</th>
                    <th style={{ textAlign: "right" }}>Terbayar</th>
                    <th style={{ textAlign: "right" }}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {series.map((s) => (
                    <tr key={s.periode} data-testid={`row-${s.periode}`}>
                      <td style={{ fontFamily: "Consolas, monospace" }}>{s.periode}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{num(s.count)}</td>
                      <td style={{ textAlign: "right" }}>{usd(s.revenue)}</td>
                      <td style={{ textAlign: "right", color: "#4f8a10" }}>{usd(s.paid)}</td>
                      <td style={{ textAlign: "right", color: "#d8000c" }}>{usd(s.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Window>
  );
}
